import { MeetingIcon } from '@renderer/chat'
import { CalendarEvent } from '@highlight/common'
import { getMeetingAppName, getMeetingIcon } from '@shared/utils/connections/calendars'
import { memo } from 'react'
import { getEventProvider } from '@renderer/utils/calendar-helpers'
import { useCalendars, useCalendarColor, type Calendar } from '@renderer/hooks/use-calendars'
import { Logo, Tooltip } from '@highlight/ui'

interface CalendarEventIconViewProps {
  event: CalendarEvent
  size?: number
  calendars: Array<Calendar> | undefined
}

/**
 * Presentational variant: takes the calendars list as a prop. Use this from
 * components that already subscribe to useCalendars (e.g. MeetingCell) so the
 * icon doesn't mount a second calendars query subscription per cell.
 */
export const CalendarEventIconView = memo(({ event, size = 32, calendars }: CalendarEventIconViewProps) => {
  const meetingAppName = getMeetingAppName(event)
  const meetingIcon = getMeetingIcon(event)
  const provider = getEventProvider(event, calendars)

  switch (meetingAppName) {
    case 'Google Meet':
    case 'Zoom':
      return <MeetingIcon appName={meetingAppName} size={size} />
    case 'Microsoft Teams':
      return <Logo name="microsoft-teams" size={size} />
    default:
      if (meetingIcon) {
        return <img src={meetingIcon} alt="Meeting Icon" className="shrink-0" style={{ width: size, height: size }} />
      }
      // Fall back to provider-specific calendar icon
      return <Logo name={provider === 'microsoft' ? 'outlook' : 'google-calendar'} size={size} />
  }
})

CalendarEventIconView.displayName = 'CalendarEventIconView'

export const CalendarEventIcon = memo(({ event, size = 32 }: { event: CalendarEvent; size?: number }) => {
  const { calendars } = useCalendars()
  return <CalendarEventIconView event={event} size={size} calendars={calendars} />
})

CalendarEventIcon.displayName = 'CalendarEventIcon'

/** Color-coded dot for a calendar event with the calendar name shown on hover. */
export const CalendarColorDot = memo(({ calendarId }: { calendarId: string | undefined }) => {
  const { calendarColor, calendarName } = useCalendarColor(calendarId)
  if (!calendarColor) return null
  return (
    <Tooltip disabled={!calendarName}>
      <Tooltip.Trigger
        render={
          <span
            className="relative inline-block size-2 shrink-0 rounded-full shadow-sm after:absolute after:inset-[-6px] after:content-['']"
            style={{ backgroundColor: calendarColor }}
            aria-hidden="true"
          />
        }
      />
      <Tooltip.Content side="bottom">{calendarName}</Tooltip.Content>
    </Tooltip>
  )
})

CalendarColorDot.displayName = 'CalendarColorDot'
